import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, CheckCircle, X, Wrench } from 'lucide-react';
import { useDiagnosticStore } from '../system/diagnosticStore';
import { Button } from '../ui/Button';

export function SystemHealthBadge() {
  const result = useDiagnosticStore((s) => s.result);
  const modalOpen = useDiagnosticStore((s) => s.modalOpen);
  const showHealthyFlash = useDiagnosticStore((s) => s.showHealthyFlash);
  const setModalOpen = useDiagnosticStore((s) => s.setModalOpen);
  const clearHealthyFlash = useDiagnosticStore((s) => s.clearHealthyFlash);

  // Auto-dismiss the healthy flash after 3 seconds
  useEffect(() => {
    if (!showHealthyFlash) return;
    const t = setTimeout(clearHealthyFlash, 3000);
    return () => clearTimeout(t);
  }, [showHealthyFlash, clearHealthyFlash]);

  if (!result) return null;

  const issueChecks = result.checks.filter((c) => c.status !== 'pass' && !c.autoFixed);
  const issueCount = issueChecks.length;
  const needsAttention = result.status !== 'healthy' && issueCount > 0;

  return (
    <>
      {/* Amber issue badge */}
      {needsAttention && (
        <motion.button
          type="button"
          onClick={() => setModalOpen(true)}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-body cursor-pointer hover:border-gold-500/60 transition-colors"
          style={{ background: 'rgba(240,165,0,0.08)', borderColor: 'rgba(240,165,0,0.35)', color: '#F0A500' }}
        >
          <AlertTriangle size={12} />
          {issueCount} issue{issueCount === 1 ? '' : 's'} need{issueCount === 1 ? 's' : ''} attention
        </motion.button>
      )}

      {/* Transient healthy flash */}
      {showHealthyFlash && result.status === 'healthy' && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-body"
          style={{ background: 'rgba(85,239,196,0.08)', borderColor: 'rgba(85,239,196,0.35)', color: '#55EFC4' }}
        >
          <CheckCircle size={12} />
          System healthy
        </motion.div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {modalOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setModalOpen(false)}
            />
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.18 }}
            >
              <div
                className="rounded-xl border max-w-md w-full p-6 relative"
                style={{ background: '#181D35', borderColor: '#2A2F4F' }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="absolute top-3 right-3 text-content-muted hover:text-content"
                  aria-label="Close"
                >
                  <X size={16} />
                </button>

                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={16} className="text-gold-500" />
                  <h3 className="font-heading font-semibold text-base">Account diagnostic</h3>
                </div>
                <p className="text-xs text-content-secondary font-body mb-4">
                  We noticed {issueCount} {issueCount === 1 ? 'issue' : 'issues'} with your account. Most resolve themselves automatically.
                </p>

                <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                  {result.checks.map((check) => {
                    const isIssue = check.status !== 'pass' && !check.autoFixed;
                    return (
                      <div
                        key={check.name}
                        className="p-3 rounded-lg border flex items-start gap-2"
                        style={{
                          background: isIssue ? 'rgba(240,165,0,0.06)' : 'rgba(85,239,196,0.04)',
                          borderColor: isIssue ? 'rgba(240,165,0,0.25)' : '#2A2F4F',
                        }}
                      >
                        {isIssue ? (
                          <AlertTriangle size={12} className="text-gold-500 shrink-0 mt-0.5" />
                        ) : (
                          <CheckCircle size={12} className="text-[#55EFC4] shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-body font-semibold">{check.name}</div>
                          {check.message && (
                            <div className="text-[11px] text-content-secondary font-body mt-0.5">
                              {check.message}
                            </div>
                          )}
                          {check.autoFixed && (
                            <div className="text-[10px] text-[#55EFC4] font-body mt-1 inline-flex items-center gap-1">
                              <Wrench size={10} /> Auto-fixed
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Button className="w-full" size="sm" onClick={() => setModalOpen(false)}>
                  Got it
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
